import { Skeleton } from "@/components/ui/skeleton";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@/components/ui/table";
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from "@/components/ui/card";
import {
	TooltipProvider,
	Tooltip,
	TooltipTrigger,
	TooltipContent,
} from "@radix-ui/react-tooltip";
import ColumnVisibilityToggle from "./column-visibility-toggle";
import { TableColumn } from "./data-table";
import { ArrowUp, ArrowUpDown } from "lucide-react";
import { cn } from "@/lib/utils";

interface SkeletonTableProps {
	columsNumber: number;
	rowsNumber: number;
	title?: string;
	description?: string;
	columns?: TableColumn[];
}

export default function SkeletonTable({
	columsNumber,
	rowsNumber,
	title,
	description,
	columns,
}: SkeletonTableProps) {
	return (
		<div>
			{/* Table Skeleton */}
			<Card
				className={cn(
					"transition-opacity duration-20 w-full hidden sm:block",
				)}
			>
				<CardHeader className="gap-0 pb-4">
					<div className="flex flex-col sm:flex-row gap-4 justify-between">
						<div className="space-y-1 flex-1 min-w-0 mr-4">
							{title && (
								<TooltipProvider>
									<Tooltip>
										<TooltipTrigger asChild>
											<CardTitle className="sm:text-lg truncate cursor-default">
												{title}
											</CardTitle>
										</TooltipTrigger>
										<TooltipContent
											side="bottom"
											align="start"
											className="max-w-md"
										>
											<p>{title}</p>
										</TooltipContent>
									</Tooltip>
								</TooltipProvider>
							)}
							{description && (
								<TooltipProvider>
									<Tooltip>
										<TooltipTrigger asChild>
											<CardDescription className="text-sm truncate cursor-default">
												{description}
											</CardDescription>
										</TooltipTrigger>
										<TooltipContent
											side="bottom"
											align="start"
											className="max-w-md"
										>
											<p>{description}</p>
										</TooltipContent>
									</Tooltip>
								</TooltipProvider>
							)}
						</div>
						<div className="text-right">
							<Skeleton className="h-8 w-30" />
						</div>
					</div>
				</CardHeader>
				<CardContent className="pb-0">
					<div className="overflow-x-auto -mx-4 sm:mx-0">
						<div className="inline-block min-w-full align-middle">
							<Table>
								<TableHeader className="rounded-t-lg">
									<TableRow>
										{columns
											? columns.map((column, index) => (
													<TableHead
														key={column.key}
														className={`text-gray-500 dark:text-gray-300 ${column.className} ${
															column.sortable
																? "cursor-pointer hover:text-gray-700 dark:hover:text-gray-100 transition-colors"
																: ""
														}`}
													>
														<div className="flex items-center gap-2 select-none">
															<span>{column.label}</span>
															{column.sortable && (
																<ArrowUpDown className="w-4 h-4 opacity-50" />
															)}
														</div>
													</TableHead>
												))
											: Array.from({ length: columsNumber }).map(
													(_, index) => (
														<TableHead key={index}>
															<Skeleton className="h-4 w-20" />
														</TableHead>
													),
												)}
									</TableRow>
								</TableHeader>
								<TableBody>
									{Array.from({ length: rowsNumber }).map(
										(_, index) => (
											<RowSkeleton
												key={index}
												columsNumber={columsNumber}
											/>
										),
									)}
								</TableBody>
							</Table>
						</div>
					</div>
				</CardContent>
			</Card>
		</div>
	);
}

const RowSkeleton = ({ columsNumber }: { columsNumber: number }) => (
	<TableRow>
		{Array.from({ length: columsNumber }).map((_, index) => (
			<TableCell key={index}>
				<Skeleton className="h-4 w-20" />
			</TableCell>
		))}
		<TableCell className="text-right">
			<Skeleton className="h-8 w-8 rounded ml-auto" />
		</TableCell>
	</TableRow>
);
